import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DISPLAY_FONT } from '../theme';
import { SectionCard, SectionHeader, SectionLoading } from './ui/Section';

interface WordCloudProps {
  data: Array<{ text: string; value: number }>;
  title?: string;
  height?: number;
  loading?: boolean;
}

interface WordTagProps {
  text: string;
  value: number;
  maxValue: number;
  minSize?: number;
  maxSize?: number;
}

const WordTag: React.FC<WordTagProps> = ({
  text,
  value,
  maxValue,
  minSize = 13,
  maxSize = 40
}) => {
  const theme = useTheme();
  const ratio = maxValue > 0 ? value / maxValue : 0;
  // Calculate size based on value relative to max
  const size = minSize + (ratio * (maxSize - minSize));
  // Three tones instead of random hues: the most used words in plum, the
  // middle in ink, the long tail muted.
  const color = ratio > 0.55 ? theme.palette.primary.main : ratio > 0.25 ? theme.palette.text.primary : theme.palette.text.secondary;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        mx: 0.75,
        my: 0.25,
        fontFamily: DISPLAY_FONT,
        fontSize: `${size}px`,
        fontWeight: ratio > 0.25 ? 650 : 500,
        letterSpacing: '-0.01em',
        lineHeight: 1.25,
        color,
        cursor: 'default',
      }}
      title={`${text}: ${value}`}
    >
      {text}
    </Box>
  );
};

const WordCloud: React.FC<WordCloudProps> = ({
  data,
  title = 'Top Words',
  height = 400,
  loading = false
}) => {
  const header = <SectionHeader title={title} info="The words that appear most in this person's posts. Bigger and darker means more frequent." />;

  if (loading) {
    return (
      <SectionCard sx={{ height: '100%' }}>
        {header}
        <SectionLoading height={height - 80} />
      </SectionCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <SectionCard sx={{ height: '100%' }}>
        {header}
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No words to show for these filters.
        </Typography>
      </SectionCard>
    );
  }

  // Find the maximum value to scale the font sizes
  const maxValue = Math.max(...data.map(item => item.value));

  return (
    <SectionCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {header}
      <Box
        sx={{
          flex: 1,
          maxHeight: height,
          overflowY: 'auto',
          textAlign: 'center',
          alignContent: 'center',
        }}
      >
        {data.map((item, index) => (
          <WordTag
            key={index}
            text={item.text}
            value={item.value}
            maxValue={maxValue}
          />
        ))}
      </Box>
    </SectionCard>
  );
};

export default WordCloud;
